import { html, View, CustomEventWithDetail } from 'rune-ts';
import { SolutionsPageData } from '@services/MarppleShopAdmin/pages/Solutions/SolutionsPage.ts';
import { pipe, map, toArray, filter } from '@fxts/core';
import format from 'date-fns/format/index.js';
import addDays from 'date-fns/addDays/index.js';
import isAfter from 'date-fns/isAfter/index.js';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays/index.js';
import klass from './SolutionsViews.module.scss';
import { font } from '@services/MarppleShop/shared/typography';
import { MarppleShopAdminSolutionsTableOption } from '@services/MarppleShopAdmin/pages/Solutions/views/MarppleShopAdminSolutionsTable.ts';
import { Solutions } from '@services/MarppleShop/entities/pool/public/solutions.ts';
import { doIf, htmlIfElse } from '@services/MarppleShop/shared/util';
import { searchSolutionListByGroup } from '@services/MarppleShopAdmin/features/Solutions/repository';
import {
  SOLUTION_THREAD_STATUS,
  SolutionHelper,
} from '@services/MarppleShop/renderApp/components/cells/Solution/SolutionHelper.ts';
import { makeMainUrl } from '@services/MarppleShop/shared/util/url.ts';

export const SOLUTION_TYPE = {
  COPYRIGHT: 'copyright',
  FAKE_PRODUCT: 'fake-product',
  INTERNAL_REGULATION: 'internal-regulation',
} as const;

export const SOLUTION_USER_TYPE = {
  INDIVIDUAL: 'person',
  GROUP: 'group',
} as const;

export class SolutionClickEvent extends CustomEventWithDetail<
  SolutionsPageData['stores'][number]['solutions'][number]
> {}

interface MarppleShopAdminBadgeProps {
  type: 'answer' | 'not_answer' | 'suspension' | 'not_suspension' | 'selling' | 'not_selling';
}

export class MarppleShopAdminSolutions extends View<SolutionsPageData['stores'][number]> {
  options: MarppleShopAdminSolutionsTableOption;
  constructor(props: SolutionsPageData['stores'][number], options: MarppleShopAdminSolutionsTableOption) {
    super({ ...props });
    this.options = options;
  }

  protected override template() {
    return html`
      <div class="${klass.solutions}">
        <h3 class="${font('14_bold')}">상세 신고 내역</h3>
        <ul class="${klass.solutions_list}"></ul>
      </div>
    `;
  }

  protected override onMount() {
    const $list = this.element().querySelector(`.${klass.solutions_list}`);

    if ($list) {
      $list.append(
        ...pipe(
          this.data.solutions,
          filter(this.filtering()),
          map((solution) => new MarppleShopAdminSolution(solution).render()),
          toArray,
        ),
      );
    }
  }

  filtering() {
    const { status } = this.options;

    if (status === 'ANSWER') return (solution: Solutions) => !!solution.is_answer;
    if (status === 'NOT_ANSWER') return (solution: Solutions) => !solution.is_answer;

    return () => true;
  }
}

export class MarppleShopAdminSolution extends View<
  SolutionsPageData['stores'][number]['solutions'][number] &
    Omit<Partial<Awaited<ReturnType<typeof searchSolutionListByGroup>>[number]>, 'store_id'> & {
      comment?: string;
    }
> {
  protected override template() {
    return html`
      <div class="${klass.solution} ${font('14_medium')}">
        <div class="${klass.solution_header}">
          <div class="${klass.solution_badges}">
            ${htmlIfElse(
              SolutionHelper.isAnswer(this.data),
              new MarppleShopAdminBadge({ type: 'answer' }),
              new MarppleShopAdminBadge({ type: 'not_answer' }),
            )}
            ${htmlIfElse(
              SolutionHelper.isStoreSolution(this.data),
              htmlIfElse(
                SolutionHelper.isStoreSuspension(this.data),
                new MarppleShopAdminBadge({ type: 'suspension' }),
                new MarppleShopAdminBadge({ type: 'not_suspension' }),
              ),
              htmlIfElse(
                SolutionHelper.isProductSuspension(this.data),
                new MarppleShopAdminBadge({ type: 'not_selling' }),
                new MarppleShopAdminBadge({ type: 'selling' }),
              ),
            )}
          </div>
          <div class="${klass.solution_thread_status}">${this.getSolutionThreadStatus()}</div>
        </div>
        <div class="${klass.solution_body}">
          <div class="${klass.solution_title}">
            <h2 class="${font('20_bold')}">${this.getSolutionType()}</h2>
            <span>${format(new Date(this.data.created_at), 'yyyy.MM.dd HH:mm')}</span>
          </div>
          <div class="${klass.solution_detail}">
            ${doIf(
              () => html`
                <div class="${klass.solution_detail_item}">
                  <div class="${klass.solution_detail_item_label}">
                    <span>${ET('crew::solutions::item::report_type')}</span>
                  </div>
                  <div class="${klass.solution_detail_item_text}">
                    <span>${this.getSolutionType()} / ${this.getSolutionUserType()}</span>
                  </div>
                </div>
              `,
              !!this.data.user_type,
            )}
            ${doIf(
              () => html`
                <div class="${klass.solution_detail_item}">
                  <div class="${klass.solution_detail_item_label}">
                    <span>${ET('crew::solutions::item::reported_creator')}</span>
                  </div>
                  <div class="${klass.solution_detail_item_text}"><span>${this.data.store_name}</span></div>
                </div>
              `,
              !!this.data.store_name,
            )}
            ${doIf(
              () => html`
                <div class="${klass.solution_detail_item}">
                  <div class="${klass.solution_detail_item_label}">
                    <span>${ET('crew::solutions::item::reportor_email')}</span>
                  </div>
                  <div class="${klass.solution_detail_item_text}"><span>${this.data.email}</span></div>
                </div>
              `,
              !!this.data.email,
            )}
            ${doIf(
              () => html`
                <div class="${klass.solution_detail_item}">
                  <div class="${klass.solution_detail_item_label}">
                    <span>${ET('crew::solutions::item::reported_product_url')}</span>
                  </div>
                  <div class="${klass.solution_detail_item_text}">
                    <span
                      >${makeMainUrl(
                        `/${ET.lang}/${this.data.domain_name}/products/${this.data.product_id}`,
                      )}</span
                    >
                  </div>
                </div>
              `,
              !!this.data.product_id,
            )}
            ${doIf(
              () => html`
                <div class="${klass.solution_detail_item}">
                  <div class="${klass.solution_detail_item_label}">
                    <span>${ET('crew::solutions::item::report_detail')}</span>
                  </div>
                  <div class="${klass.solution_detail_item_text}"><span>${this.data.comment}</span></div>
                </div>
              `,
              !!this.data.comment,
            )}
          </div>
        </div>
      </div>
    `;
  }

  protected override onRender() {
    this.addEventListener('click', () => {
      this.dispatchEvent(SolutionClickEvent, { detail: this.data, bubbles: true });
    });
  }

  getSolutionType() {
    if (this.data.type === SOLUTION_TYPE.COPYRIGHT) return ET('crew::suspensions::type::copyright');
    if (this.data.type === SOLUTION_TYPE.FAKE_PRODUCT) return ET('crew::suspensions::type::counterfeit');
    if (this.data.type === SOLUTION_TYPE.INTERNAL_REGULATION)
      return ET('crew::suspensions::type::internal_policy_violation');
  }

  getSolutionUserType() {
    if (this.data.user_type === SOLUTION_USER_TYPE.INDIVIDUAL)
      return ET('crew::suspensions::user_type::individual');
    if (this.data.user_type === SOLUTION_USER_TYPE.GROUP) return ET('crew::suspensions::user_type::group');
  }

  getSolutionThreadStatus() {
    const solution_thread_status = SolutionHelper.thread_status(this.data);
    const today = new Date();
    const deadline = addDays(
      SolutionHelper.isAnswer(this.data)
        ? new Date(this.data.last_answer_at || today)
        : new Date(this.data.last_request_at || today),
      14,
    );
    const day = isAfter(today, deadline) ? 0 : differenceInCalendarDays(deadline, today);

    if (solution_thread_status === SOLUTION_THREAD_STATUS.END)
      return ET('crew::solutions::thread_status::end');
    if (solution_thread_status === SOLUTION_THREAD_STATUS.ANSWER_EXPIRATION)
      return ET('crew::solutions::thread_status::answer_expiration');
    if (solution_thread_status === SOLUTION_THREAD_STATUS.IN_PROGRESS)
      return html`<b class="${klass.solution_thread_status_in_progress}"
        >${ET('crew::solutions::thread_status::in_progress', { day })}</b
      >`;
    if (solution_thread_status === SOLUTION_THREAD_STATUS.RE_IN_PROGRESS)
      return html`<b class="${klass.solution_thread_status_in_progress}"
        >${ET('crew::solutions::thread_status::re_in_progress', { day })}</b
      >`;

    throw new Error('Not Exist Solution Thread Status.');
  }
}

export class MarppleShopAdminBadge extends View<MarppleShopAdminBadgeProps> {
  protected override template() {
    return html`
      <div class="${klass.badge} ${this.getBadgeStyle()}">
        <span class="${font('14_medium')}">${this.getBadgeText()}</span>
      </div>
    `;
  }

  getBadgeStyle() {
    const BADGE_STYLE_MAP: Record<MarppleShopAdminBadgeProps['type'], string> = {
      answer: klass.answer,
      not_answer: klass.not_answer,
      suspension: klass.suspension,
      not_suspension: klass.not_suspension,
      selling: klass.not_suspension,
      not_selling: klass.suspension,
    } as const;

    return BADGE_STYLE_MAP[this.data.type];
  }

  getBadgeText() {
    const BADGE_TEXT_MAP: Record<MarppleShopAdminBadgeProps['type'], string> = {
      answer: ET('crew::solutions::status::answer'),
      not_answer: ET('crew::solutions::status::not_answer'),
      suspension: ET('crew::solutions::status::suspension'),
      not_suspension: ET('crew::solutions::status::not_suspension'),
      selling: ET('crew::solutions::status::selling'),
      not_selling: ET('crew::solutions::status::not_selling'),
    } as const;

    return BADGE_TEXT_MAP[this.data.type];
  }
}
